import { useState } from "react";
import ModalLayout from "../layouts/ModalLayout";
import CustomButton from "../CustomButton";
import { LeaveRequestItem } from "../../types/dashboardType";

interface Props {
  leaveRequests: LeaveRequestItem[];
  onClose?: () => void;
  onStatusChange: (
    leaveRequestId: number,
    status: "APPROVED" | "REJECTED",
  ) => void;
}

function LeaveRequestModal({ leaveRequests, onClose,onStatusChange }: Props) {
  const [activeTab, setActiveTab] = useState<"staff" | "student">("staff");

  const staffLeaves = leaveRequests.filter((l) => l.staffId !== null);
  const studentLeaves = leaveRequests.filter((l) => l.studentId !== null);

  const filteredData = activeTab === "staff" ? staffLeaves : studentLeaves;

  return (
    <ModalLayout onClose={onClose}>
      <div className="bg-background-50 rounded-2xl">
        <div className="pb-4">
          <p className="text-base font-semibold text-neutral-500">
            Leave Requests
          </p>
          <p className="text-xs text-neutral-300 mt-0.5">
            Review and respond to pending requests
          </p>
        </div>
        <div className="flex text-xs font-medium bg-background-100 border-b border-neutral-100">
          {(["staff", "student"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 capitalize transition-all ${
                activeTab === tab
                  ? "text-primary-600 border-b-2 border-primary-600 bg-background-50"
                  : "text-neutral-300 hover:text-neutral-400"
              }`}
            >
              {tab === "staff" ? "Staff" : "Students"}
            </button>
          ))}
        </div>

        {/* CONTENT */}
        <div className="max-h-[350px] overflow-y-auto px-6 py-5 flex flex-col gap-5">
          {filteredData.length === 0 ? (
            <div className="flex flex-col items-center gap-1 py-12 text-center">
              <p className="text-sm font-medium text-neutral-300">
                No pending requests
              </p>
              <p className="text-xs text-neutral-200">
                Everything is running as scheduled.
              </p>
            </div>
          ) : (
            filteredData.map((item) => {
              const name =
                activeTab === "staff" ? item.staffName : item.studentName;

              return (
                <div
                  key={item.leaveRequestId}
                  className="flex items-start gap-3"
                >
                  {/* AVATAR */}
                  <div className="w-9 h-9 rounded-full text-white bg-primary-600 flex items-center justify-center font-semibold text-sm shrink-0">
                    {name?.charAt(0)?.toUpperCase() || "?"}
                  </div>

                  <div className="flex flex-col w-full gap-1">
                    <p className="text-sm text-neutral-400">
                      <span className="font-semibold text-neutral-500">
                        {name}
                      </span>{" "}
                      <span className="text-neutral-300">
                        is requesting leave
                      </span>
                    </p>

                    <p className="text-xs text-neutral-300 line-clamp-2 leading-relaxed">
                      {item.reason}
                    </p>

                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <button
                        onClick={() =>
                          onStatusChange(item.leaveRequestId, "APPROVED")
                        }
                        className="text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg py-2 transition-colors"
                      >
                        Approve
                      </button>

                      <button
                        onClick={() =>
                          onStatusChange(item.leaveRequestId, "REJECTED")
                        }
                        className="text-xs font-medium text-error-600 bg-error-50 hover:bg-error-100 rounded-lg py-2 transition-colors"
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 pt-2 flex justify-end bg-background-50">
          <CustomButton
            color="bg-neutral-200"
            text="Cancel"
            onClick={onClose}
          />
        </div>
      </div>
    </ModalLayout>
  );
}

export default LeaveRequestModal;
